import React, { useEffect, useState, useCallback, useRef } from 'react';
// service
import { getList } from 'services/product.service';
// components
import _ from 'lodash';
import DataTable from 'components/shared/DataTable/tableChange';
import PromotionProductFilter from './PromotionProductFilter';
import { defaultPaging, defaultParams } from 'utils/helpers';
import { showConfirmModal } from 'actions/global';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import ICON_COMMON from 'utils/icons.common';
import BWImage from 'components/shared/BWImage/index';
import { notification } from 'antd';
import FormNumber from 'components/shared/BWFormControl/FormNumber';
import { normalizePromotionGiftRows } from 'pages/PromotionOffers/utils/promotionGiftQuantity';

const getImeiValue = (imei) => {
  if (!imei || React.isValidElement(imei)) return null;
  if (typeof imei === 'object') {
    return imei.imei || imei.imei_code || imei.product_imei_code || imei.value || imei.label || imei.code || imei.sku;
  }

  return imei;
};

const normalizeProductImeis = (product = {}) => {
  const rawImeis =
    product?.imeis ??
    product?.imei_code_options ??
    product?.imei_list ??
    product?.imei_codes ??
    product?.product_imei_codes ??
    product?.product_imei_code ??
    product?.imei ??
    product?.serials ??
    product?.serial;

  const imeis = Array.isArray(rawImeis)
    ? rawImeis.map(getImeiValue)
    : `${rawImeis || ''}`.split(/[,\n;|]+/).map((item) => item.trim());

  return [...new Set(imeis.map((item) => `${item || ''}`.trim()).filter(Boolean))];
};

const formatNumber = (value) => `${Number(value || 0)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const getProductImeiLabel = (product = {}) => {
  const imeis = normalizeProductImeis(product);
  if (!imeis.length) return '';

  const visibleImeis = imeis.slice(0, 4).join(', ');
  return imeis.length > 4 ? `${visibleImeis} +${imeis.length - 4}` : visibleImeis;
};

const getProductStockQuantity = (product = {}) =>
  Number(product?.stock_quantity || product?.available_quantity || product?.total_inventory || product?.inventory || 0);

const getProductStockSummary = (product = {}) => {
  if (product?.stock_summary) return product.stock_summary;

  const stockOptions = product?.stock_options || product?.stocks || [];
  if (!Array.isArray(stockOptions)) return '';

  return stockOptions
    .map((stock) => {
      const stockName =
        stock?.stock_name || stock?.stocks_name || stock?.name || stock?.stock_code || stock?.stocks_code;
      const quantity = Number(stock?.quantity || stock?.available_quantity || 0);
      return stockName && quantity > 0 ? `${stockName}: ${formatNumber(quantity)}` : '';
    })
    .filter(Boolean)
    .join(' | ');
};

export default function PromotionsProductTable({
  handleOpenModalGift,
  noLoadData,
  contentCreate,
  showCreateContent = false,
  fieldProduct,
  hiddenAction,
  validation,
  onClose = () => {},
  onSelectionChange,
  disabled,
  showDefaultQuantity = false,
}) {
  const dispatch = useDispatch();
  const methods = useFormContext();
  const [loading, setLoading] = useState(false);
  const [dataProduct, setDataProduct] = useState(defaultPaging);
  const [valueMomentary, setValueMomentary] = useState([]);
  const [isAllPagesSelected, setIsAllPagesSelected] = useState(false);
  const selectedAllProductIdsRef = useRef(new Set());
  const selectedProducts = methods.watch(fieldProduct);

  useEffect(() => {
    if (!showDefaultQuantity || !noLoadData || !Array.isArray(selectedProducts)) return;

    const normalizedProducts = normalizePromotionGiftRows(selectedProducts);
    if (!_.isEqual(selectedProducts, normalizedProducts)) {
      methods.setValue(fieldProduct, normalizedProducts, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    }
  }, [fieldProduct, methods, noLoadData, selectedProducts, showDefaultQuantity]);

  const [query, setQuery] = useState({
    ...defaultParams,
    include_imeis: 1,
    itemsPerPage: 10,
  });

  const { items, itemsPerPage, page, totalItems, totalPages } = dataProduct;

  const handleChangePage = (page) => {
    setQuery({ ...query, page });
  };
  const columns = React.useMemo(
    () => [
      {
        header: 'Mã hàng hóa - vật tư',
        classNameHeader: 'bw_text_center',
        styleHeader: { width: 155 },
        style: { width: 155 },
        accessor: 'product_code',
        formatter: (p) => <b className='bw_sticky bw_name_sticky'>{p?.product_code}</b>,
      },
      {
        header: 'Tên hàng hóa - vật tư',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_promotion_product_name_col',
        styleHeader: { width: 440 },
        style: { width: 440 },
        formatter: (p) => {
          const imeiLabel = getProductImeiLabel(p);

          return (
            <div className='bw_inf_pro bw_promotion_product_info'>
              <BWImage src={p?.picture_url}></BWImage>
              <div className='bw_promotion_product_text'>
                <b>{p?.product_name}</b>
                {noLoadData ? (
                  <span className='bw_promotion_product_meta'>
                    {p?.product_code ? <span>Mã SP: {p.product_code}</span> : null}
                    {p?.unit_name ? <span>Đơn vị: {p.unit_name}</span> : null}
                  </span>
                ) : null}
                {imeiLabel ? <span className='bw_promotion_product_imei'>IMEI/Serial: {imeiLabel}</span> : null}
              </div>
            </div>
          );
        },
      },
      {
        header: 'Tồn kho',
        classNameBody: 'bw_text_center',
        classNameHeader: 'bw_text_center',
        styleHeader: { width: 170 },
        style: { width: 170 },
        formatter: (p) => {
          const stockQuantity = getProductStockQuantity(p);
          const imeiCount = normalizeProductImeis(p).length;
          const stockSummary = getProductStockSummary(p);

          return (
            <div style={{ display: 'grid', gap: 4, justifyItems: 'center', minWidth: 120 }}>
              <b>{formatNumber(stockQuantity)}</b>
              <span style={{ color: '#667085', fontSize: 12 }}>
                {imeiCount ? `${imeiCount} IMEI/Serial` : 'Theo số lượng'}
              </span>
              {stockSummary ? (
                <span style={{ color: '#667085', fontSize: 12, maxWidth: 220, whiteSpace: 'normal' }}>
                  {stockSummary}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        header: 'Số lượng mặc định',
        hidden: !showDefaultQuantity,
        classNameBody: 'bw_text_center',
        classNameHeader: 'bw_text_center',
        styleHeader: { width: 150 },
        style: { width: 150 },
        formatter: (_product, index) => (
          <FormNumber
            field={`${fieldProduct}.${index}.default_quantity`}
            disabled={disabled}
            min={1}
            precision={0}
            validation={{
              required: 'Vui lòng nhập số lượng mặc định',
              min: { value: 1, message: 'Số lượng mặc định phải lớn hơn 0' },
            }}
          />
        ),
      },
      {
        header: 'Thuộc ngành hàng',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_promotion_product_text_col',
        styleHeader: { width: 190 },
        style: { width: 190 },
        formatter: (p) => p?.category_name,
      },
      {
        header: 'Hãng',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_promotion_product_text_col',
        styleHeader: { width: 140 },
        style: { width: 140 },
        formatter: (p) => p?.manufacture_name,
      },
      {
        header: 'Ngày tạo',
        classNameBody: 'bw_text_center bw_promotion_product_text_col',
        classNameHeader: 'bw_text_center',
        styleHeader: { width: 130 },
        style: { width: 130 },
        formatter: (p) => p?.created_date,
      },
      {
        hidden: noLoadData,
        header: 'Trạng thái',
        accessor: 'status',
        classNameBody: 'bw_text_center',
        classNameHeader: 'bw_text_center',
        styleHeader: { width: 105 },
        style: { width: 105 },
        formatter: (p) =>
          p?.is_active ? (
            <span className='bw_label_outline bw_label_outline_success text-center'>Kích hoạt</span>
          ) : (
            <span className='bw_label_outline bw_label_outline_danger text-center'>Ẩn</span>
          ),
      },
    ],
    [disabled, fieldProduct, noLoadData, showDefaultQuantity],
  );

  const getProductList = useCallback(() => {
    setLoading(true);
    getList(query)
      .then((data) => setDataProduct(data))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(getProductList, [getProductList]);

  const handleToggleSelectAllPages = useCallback(
    async (checked, currentSelection = []) => {
      setLoading(true);
      try {
        const response = await getList({
          ...query,
          page: 1,
          itemsPerPage: Math.max(Number(totalItems) || Number(itemsPerPage) || 1, 1),
        });
        const matchingProducts = Array.isArray(response?.items) ? response.items : [];
        const matchingIds = new Set(matchingProducts.map((item) => String(item?.product_id)));
        const selectionOutsideCurrentFilter = (Array.isArray(currentSelection) ? currentSelection : []).filter(
          (item) => !matchingIds.has(String(item?.product_id)),
        );
        const nextSelection = checked
          ? Array.from(
              new Map(
                [...selectionOutsideCurrentFilter, ...matchingProducts].map((item) => [String(item?.product_id), item]),
              ).values(),
            )
          : selectionOutsideCurrentFilter;

        selectedAllProductIdsRef.current = checked ? matchingIds : new Set();
        setIsAllPagesSelected(checked && matchingProducts.length > 0);
        setValueMomentary(nextSelection);
        return nextSelection;
      } catch (error) {
        notification.error({
          message: error?.message || 'Không thể chọn sản phẩm của toàn bộ các trang.',
        });
        return currentSelection;
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage, query, totalItems],
  );

  const handleSelectionChange = useCallback(
    (selection = []) => {
      onSelectionChange?.(selection);
      if (!isAllPagesSelected || !selectedAllProductIdsRef.current.size) return;

      const selectedIds = new Set(selection.map((item) => String(item?.product_id)));
      const stillSelectedAllPages = Array.from(selectedAllProductIdsRef.current).every((id) => selectedIds.has(id));
      if (!stillSelectedAllPages) {
        setIsAllPagesSelected(false);
      }
    },
    [isAllPagesSelected, onSelectionChange],
  );

  const actions = [
    {
      hidden: !noLoadData,
      globalAction: true,
      icon: 'fi fi-rr-plus',
      color: 'orange',
      permission: 'SM_COUPOND_ADD_PRODUCT',
      content: contentCreate,
      showContent: showCreateContent,
      onClick: handleOpenModalGift,
    },
    {
      icon: 'fi fi-rr-eye',
      color: 'green',
      hidden: hiddenAction?.detail,
      onClick: (item) => window.open(`/product/detail/${item.product_id}`),
    },
    {
      icon: ICON_COMMON.trash,
      color: 'red',
      hidden: hiddenAction?.delete,
      permission: 'PROMOTIONS_OFFERS_DEL',
      onClick: (value) => {
        dispatch(
          showConfirmModal([`Xoá ${value?.store_name} ra khỏi danh sách khách hàng áp dụng ?`], () => {
            const promotion_offer_product = _.cloneDeep(methods.watch(fieldProduct));
            methods.setValue(
              fieldProduct,
              promotion_offer_product.filter((o) => parseInt(o?.product_id) !== parseInt(value?.product_id)),
            );
            return;
          }),
        );
      },
    },
  ];
  return (
    <>
      <div className='bw_main_wrapp'>
        {!noLoadData && (
          <PromotionProductFilter
            onChange={(value) => {
              selectedAllProductIdsRef.current = new Set();
              setIsAllPagesSelected(false);
              setQuery({
                ...query,
                ...value,
              });
            }}
          />
        )}
        <DataTable
          hiddenDeleteClick
          noSelect={noLoadData}
          noPaging={noLoadData}
          dataSelectChange={valueMomentary}
          fieldCheck='product_id'
          defaultDataSelect={!noLoadData ? methods.watch(fieldProduct) : []}
          loading={loading}
          columns={columns}
          data={noLoadData ? methods.watch(fieldProduct) : items}
          actions={actions}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          page={page}
          totalItems={totalItems}
          onChangePage={handleChangePage}
          onChangeSelect={handleSelectionChange}
          onToggleSelectAllPages={!noLoadData ? handleToggleSelectAllPages : undefined}
          isAllPagesSelected={isAllPagesSelected}
          selectAllPagesLabel={`Chọn tất cả ${Number(totalItems) || 0} sản phẩm của toàn bộ các trang`}
          handleBulkAction={(e) => {
            methods.setValue(fieldProduct, e);
          }}
          deleteAllData={true}
        />
      </div>
    </>
  );
}
